import { Icon } from "@/components/Icon/Icon";
import type { ReplacementPreview } from "@/utils/formulation/computeCurrent";

type ReplacementPreviewBannerProps = {
  preview: ReplacementPreview;
  onApply: () => void;
  onCancel: () => void;
};

export function ReplacementPreviewBanner({
  preview,
  onApply,
  onCancel,
}: ReplacementPreviewBannerProps) {
  const deltaLoss = preview.deltaLoss ?? 0;
  const isImprovement = deltaLoss < 0;
  // Banner doesn't carry baseLoss; use an absolute sqrt-scale cutoff. 0.5 is
  // a substantial regression in sqrt-scale Loss; anything beyond reads red.
  // (Tuned to roughly match the prior squared-scale "+8" red threshold for
  // typical flask magnitudes around lossSq~25.)
  const accent = isImprovement
    ? "var(--brand-green)"
    : deltaLoss > 0.5
    ? "#c92619"
    : "#d97706";
  const accentBg = isImprovement
    ? "rgba(27,58,43,0.06)"
    : deltaLoss > 0.5
    ? "rgba(201,38,25,0.06)"
    : "rgba(217,119,6,0.08)";
  const accentBorder = isImprovement
    ? "rgba(27,58,43,0.22)"
    : deltaLoss > 0.5
    ? "rgba(201,38,25,0.22)"
    : "rgba(217,119,6,0.28)";

  return (
    <div
      className="preview-banner"
      style={{ background: accentBg, border: `1px solid ${accentBorder}` }}
    >
      <span
        className="preview-banner-stripe"
        style={{ background: accent }}
      />
      <span
        className="preview-banner-tag"
        style={{ background: accent }}
      >
        PREVIEW
      </span>

      <div
        className="vstack"
        style={{ flex: 1, minWidth: 0, lineHeight: 1.25 }}
      >
        <span
          style={{
            fontSize: 12.5,
            color: "var(--ink)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          Replace{" "}
          <strong style={{ fontWeight: 600 }}>{preview.sourceName}</strong>
          <span style={{ color: "var(--ink-4)" }}> → </span>
          <strong style={{ fontWeight: 600, color: accent }}>
            {preview.isDrop ? "remove from flask" : preview.replName}
          </strong>
        </span>
        <span className="mono" style={{ fontSize: 10, color: "var(--ink-4)" }}>
          Sensory plots updated · review the change before committing
        </span>
      </div>

      <div
        className="hstack"
        style={{
          gap: 6,
          alignItems: "center",
          padding: "0 8px",
          borderLeft: `1px solid ${accentBorder}`,
        }}
      >
        <span
          className="mono"
          style={{
            fontSize: 9,
            color: "var(--ink-4)",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
          }}
        >
          Δ loss
        </span>
        <span
          className="mono"
          style={{ fontSize: 14, color: accent, fontWeight: 700 }}
        >
          {deltaLoss > 0 ? "+" : ""}
          {deltaLoss.toFixed(2)}
        </span>
      </div>

      <button
        type="button"
        onClick={onCancel}
        className="btn xs ghost"
        style={{ padding: "5px 10px" }}
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={onApply}
        className="btn xs"
        style={{
          padding: "5px 12px",
          background: accent,
          color: "var(--brand-cream)",
          border: `1px solid ${accent}`,
          fontWeight: 500,
        }}
      >
        <Icon name="check" size={11} />
        Apply replacement
      </button>
    </div>
  );
}
